// hooks/useLoginCartHandler.js
import { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { createEvent } from "../../../Apiservices/Auth/servers/createEvents";
import { addPriestToCartAPI, DirectaddTentToCartAPI, directaddCateringToCartAPI } from "../../../Apiservices/Auth/cart/Addtocart_api";
import { resetServiceBooking, setShowEventTabs } from "../../../store/serviceBookingSlice";
import { 
  clearSelectedProviders,
  closeServiceDialog, 
  setCommonData, 
  setServiceType,
  openServiceDialog,
  openProviderDialog,
  setShouldProcessCartAfterLogin
} from '../../../store/serviceBookingSlice';

export const useLoginCartHandler = () => {
  const dispatch = useDispatch();
  const { 
    selectedProviders, 
    commonData,
    shouldProcessCartAfterLogin
  } = useSelector(state => state.serviceBooking);
  
  const [eventCreated, setEventCreated] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  
  // Use refs to track processed state without causing re-renders
  const hasProcessedRef = useRef(false);
  const processingRef = useRef(false);

  // Helper functions
  const addPriestProvidersToEvent = async (eventId, providers) => {
    console.log(`Adding ${providers.length} priest providers to event ${eventId}`);
    
    for (const provider of providers) {
      try {
        await addPriestToCartAPI({ 
          event_id: eventId,
          priest_id: provider.id,
          service_id: commonData.eventId || null,
        });
        console.log(`Added priest provider ${provider.id} to event ${eventId}`);
      } catch (error) {
        console.error(`Failed to add priest provider ${provider.id} to event:`, error);
        throw error;
      }
    }
  };

  const addTentProvidersDirectly = async (providers) => {
    console.log(`Adding ${providers.length} tent providers directly...`, commonData);
    
    for (const provider of providers) {
      try {
        const requestData = {
          provider_id: provider.id,
          items: provider.selectedTentItems || provider.selectedItems || [],
          // Include event details for quick event creation
          eventTitle: commonData.whoseEvent || "Quick Tent Booking",
          startDate: commonData.eventDate ? new Date(commonData.eventDate).toISOString() : null,
          venue: commonData.address || "",
          locationLat: commonData.latitude || null,
          locationLong: commonData.longitude || null,
          area: commonData.area || "",
          city: commonData.city || "",
          state: commonData.state || "",
        };

        console.log("Sending tent provider data:", requestData);
        const result = await DirectaddTentToCartAPI(requestData);
        console.log(`Added tent provider ${provider.id} directly:`, result);
      } catch (error) {
        console.error(`Failed to add tent provider ${provider.id}:`, error);
        throw error;
      }
    }
  };

  const addCateringProvidersDirectly = async (providers) => {
    console.log(`Adding ${providers.length} catering providers directly...`);
    
    for (const provider of providers) {
      try {
        const requestData = {
          provider_id: provider.id,
          items: [
            { 
              mapping_id: provider.itemId, 
              number_of_people: commonData.cateringQuantity || 10 
            }
          ],
          // Include event details for quick event creation
          eventTitle: commonData.whoseEvent || "Quick Catering Booking",
          startDate: commonData.eventDate ? new Date(commonData.eventDate).toISOString() : null,
          venue: commonData.address || "",
          locationLat: commonData.latitude || null,
          locationLong: commonData.longitude || null,
          area: commonData.area || "",
          city: commonData.city || "",
          state: commonData.state || "",
        };

        console.log("Sending catering provider data:", requestData);
        const result = await directaddCateringToCartAPI(requestData);
        console.log(`Added catering provider ${provider.id} directly:`, result);
      } catch (error) {
        console.error(`Failed to add catering provider ${provider.id}:`, error);
        throw error;
      }
    }
  };

  const handleCartAfterLogin = useCallback(async () => {
    // Check if there are items in cart and we have basic event data
    if (hasProcessedRef.current || selectedProviders.length === 0 || processingRef.current) {
      console.log("Cart already processed, no providers, or already processing - skipping");
      return;
    }
    
    const hasCartItems = selectedProviders.length > 0;
    const hasEventData = commonData.address && commonData.eventDate && commonData.whoseEvent;
    
    // CHECK: Verify we have processing flags
    const localStorageFlag = localStorage.getItem('shouldProcessCartAfterLogin');
    const pendingCartData = localStorage.getItem('pendingCartData');
    const isComingFromLogin = localStorage.getItem('justLoggedIn') === 'true';
    
    // Check if we should process based on ANY flag
    const shouldProcess = shouldProcessCartAfterLogin || localStorageFlag || pendingCartData || isComingFromLogin;
    
    if (!shouldProcess) {
      console.log("No cart processing flags found - skipping auto-process");
      return;
    }
    
    if (hasCartItems && hasEventData) {
      console.log("Cart items found with processing flag, processing...", commonData);
      
      processingRef.current = true;
      setProcessing(true);
      setEventCreated(false);
      setError(null);
      
      await new Promise(resolve => setTimeout(resolve, 1000));

      try {
        // Separate providers by type
        const priestProviders = selectedProviders.filter(p => p.serviceType === 'priest');
        const tentProviders = selectedProviders.filter(p => p.serviceType === 'tent');
        const cateringProviders = selectedProviders.filter(p => p.serviceType === 'catering');

        let eventId = null;

        // Step 1: For priest services, create event first
        if (priestProviders.length > 0) {
          console.log("Priest providers found, creating event first...");
          
          const eventDetails = {
            eventTitle: commonData.whoseEvent || "Event Booking",
            event: {
              event: commonData.eventName || "",
              id: commonData.eventId || null,
            },
            eventDesc: commonData.whoseEvent || "",
            Address: commonData.address || "",
            Date: commonData.eventDate ? new Date(commonData.eventDate).toISOString() : null,
            endDate: null,
            locationLat: commonData.latitude || null,
            locationLong: commonData.longitude || null,
            area: commonData.area || "",
            city: commonData.city || "",
            state: commonData.state || "",
          };

          console.log("Creating event with details:", eventDetails);
          
          try {
            const createdEvent = await createEvent(eventDetails);
            console.log("Event created successfully:", createdEvent);
            
            eventId = createdEvent.event?.id || createdEvent.id;
            setEventCreated(true);
            
            // Add priest providers to the created event
            if (eventId) {
              await addPriestProvidersToEvent(eventId, priestProviders);
            }
            
          } catch (eventError) {
            console.error("Failed to create event:", eventError);
            if (eventError.message && eventError.message.includes('generateUniqueToken')) {
              console.error("Backend error: generateUniqueToken function is missing");
            }
            setError(eventError.message);
            throw eventError;
          }
        }

        // Step 2: For tent services, send directly to API (no event creation needed)
        if (tentProviders.length > 0) {
          console.log("Processing tent providers...");
          await addTentProvidersDirectly(tentProviders);
        }

        // Step 3: For catering services, send directly to API (no event creation needed)
        if (cateringProviders.length > 0) {
          console.log("Processing catering providers...");
          await addCateringProvidersDirectly(cateringProviders);
        }

        // Clear the cart after successful processing
        dispatch(clearSelectedProviders());
        dispatch(closeServiceDialog())
        dispatch(resetServiceBooking());
        hasProcessedRef.current = true;
        dispatch(setShowEventTabs(true));
        
        // Reset the flag
        dispatch(setShouldProcessCartAfterLogin(false));
        
        // Clear localStorage flags
        localStorage.removeItem('pendingCartData');
        localStorage.removeItem('shouldProcessCartAfterLogin');
        localStorage.removeItem('justLoggedIn');
        
      } catch (error) {
        console.error("Failed to process cart after login:", error);
        setError(error.message);
      } finally {
        processingRef.current = false;
        setProcessing(false);
      }
    } else if (hasCartItems && !hasEventData) {
      console.warn("Cart items found but missing event data. Cannot create event.");
    }
  }, [selectedProviders, commonData, shouldProcessCartAfterLogin, dispatch]);

  const handleAfterLogin = useCallback(async () => {
    // Check if we have pending service data that needs to be resumed
    const pendingServiceData = localStorage.getItem('pendingServiceData');
    
    if (pendingServiceData) {
      const serviceData = JSON.parse(pendingServiceData);
      console.log("🔄 Resuming service booking after login:", serviceData);
      
      // Set flag that we just logged in
      localStorage.setItem('justLoggedIn', 'true');
      
      // CRITICAL: Set serviceType FIRST
      dispatch(setServiceType(serviceData.serviceType));
      
      // Restore the service data to Redux
      dispatch(setCommonData(serviceData.commonData));
      
      // Open the appropriate dialog based on where user left off
      if (serviceData.currentStep === 'cateringSelection') {
        console.log("📦 Opening service dialog for catering selection");
        // Add a small delay to ensure Redux state is updated
        setTimeout(() => {
          dispatch(openServiceDialog());
        }, 50);
      } else if (serviceData.currentStep === 'providerSelection') {
        console.log("👥 Opening provider dialog directly");
        dispatch(openProviderDialog());
      }
      
      // Clear pending data
      localStorage.removeItem('pendingServiceData');
      return; // Exit early since we're handling pending service data
    }
    
    // If no pending service data, check if we should process cart
    const localStorageFlag = localStorage.getItem('shouldProcessCartAfterLogin');
    const hasCartFlag = shouldProcessCartAfterLogin || localStorageFlag;
    
    if (hasCartFlag) {
      // Process cart items
      await handleCartAfterLogin();
    } else {
      console.log("No cart processing flags set - user is likely adding new items");
    }
  }, [shouldProcessCartAfterLogin, dispatch, handleCartAfterLogin]);

  useEffect(() => {
    // Add a small delay to ensure Redux state is fully updated
    const timer = setTimeout(() => {
      handleAfterLogin();
    }, 100);
    
    return () => clearTimeout(timer);
  }, [shouldProcessCartAfterLogin, handleAfterLogin]); // Only trigger on flag changes

  return {
    eventCreated,
    processing,
    error,
    hasCartItems: selectedProviders.length > 0
  };
};